import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, ArrowRight, Package, Edit2 } from 'lucide-react';
import { Dialog } from '../../../components/Dialog';
import { productionApi } from '../../../services/api';

interface PendingItem {
  productId: string;
  productName: string;
  unit: string;
  availableQuantity: number;
  transferQuantity: number;
}

interface BulkTransferDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sourceDate: string; // YYYY-MM-DD
  pendingItems: PendingItem[];
  onSuccess: () => void;
}

export const BulkTransferDialog: React.FC<BulkTransferDialogProps> = ({
  isOpen,
  onClose,
  sourceDate,
  pendingItems,
  onSuccess,
}) => {
  const [items, setItems] = useState<PendingItem[]>([]);
  const [destinationDate, setDestinationDate] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setItems(pendingItems.map(item => ({ ...item })));
      setDestinationDate('');
      setError(null);
    }
  }, [isOpen, pendingItems]);

  const totalItems = useMemo(() => items.filter(i => i.transferQuantity > 0).length, [items]);

  const handleQuantityChange = (productId: string, unit: string, newQuantity: number) => {
    setItems(prev => prev.map(item => {
      if (item.productId === productId && item.unit === unit) {
        return {
          ...item,
          transferQuantity: Math.min(Math.max(0, newQuantity), item.availableQuantity)
        };
      }
      return item;
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validations
    if (!destinationDate) {
      setError('Debes seleccionar una fecha de destino');
      return;
    }

    if (destinationDate === sourceDate) {
      setError('La fecha de destino debe ser diferente a la de origen');
      return;
    }

    const itemsToTransfer = items.filter(i => i.transferQuantity > 0);
    if (itemsToTransfer.length === 0) {
      setError('Debes seleccionar al menos un producto para transferir');
      return;
    }

    try {
      setIsSubmitting(true);

      await productionApi.bulkTransferProduction({
        sourceDate,
        destinationDate,
        items: itemsToTransfer.map(item => ({
          productId: item.productId,
          unit: item.unit,
          quantity: item.transferQuantity,
        })),
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error bulk transferring production:', err);
      setError(err.message || 'Error al traspasar la producción');
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Transferir Pendientes"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Date Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="text-blue-600" size={20} />
            <h4 className="font-semibold text-slate-900">Transferencia Masiva</h4>
          </div>
          <p className="text-sm text-slate-600">
            Fecha origen: <span className="font-medium">{new Date(sourceDate).toLocaleDateString('es-ES')}</span>
          </p>
          <p className="text-sm text-slate-600">
            Productos pendientes: <span className="font-bold text-blue-700">{totalItems}</span>
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Destination Date */}
        <div>
          <label htmlFor="destinationDate" className="block text-sm font-medium text-slate-700 mb-1">
            Fecha de destino <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <input
              id="destinationDate"
              type="date"
              value={destinationDate}
              onChange={(e) => setDestinationDate(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              disabled={isSubmitting}
              required
            />
          </div>
        </div>

        {/* Items List */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Productos a transferir
          </label>
          <div className="max-h-96 overflow-y-auto space-y-2 border border-slate-200 rounded-lg p-3">
            {items.map((item, index) => (
              <div
                key={`${item.productId}-${item.unit}`}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  item.transferQuantity > 0
                    ? 'bg-green-50 border-green-200'
                    : 'bg-slate-50 border-slate-200'
                }`}
              >
                <Package className={item.transferQuantity > 0 ? 'text-green-600' : 'text-slate-400'} size={20} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{item.productName}</p>
                  <p className="text-xs text-slate-500">
                    Disponible: {item.availableQuantity} {item.unit}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    max={item.availableQuantity}
                    value={item.transferQuantity || ''}
                    onChange={(e) => handleQuantityChange(item.productId, item.unit, Number(e.target.value))}
                    className="w-24 px-2 py-1 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="0"
                    disabled={isSubmitting}
                  />
                  <span className="text-sm text-slate-600 min-w-[60px]">{item.unit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        {totalItems > 0 && destinationDate && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <div className="text-center flex-1">
                <p className="text-slate-600">Origen</p>
                <p className="font-bold text-slate-900">{new Date(sourceDate).toLocaleDateString('es-ES')}</p>
              </div>
              <ArrowRight className="text-blue-600" size={24} />
              <div className="text-center flex-1">
                <p className="text-slate-600">Destino</p>
                <p className="font-bold text-slate-900">{new Date(destinationDate).toLocaleDateString('es-ES')}</p>
              </div>
            </div>
            <p className="text-center text-sm font-medium text-blue-700">
              {totalItems} producto{totalItems !== 1 ? 's' : ''} a transferir
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors font-medium disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting || totalItems === 0}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting && (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            )}
            Transferir {totalItems > 0 && `(${totalItems})`}
          </button>
        </div>
      </form>
    </Dialog>
  );
};
